// ✦ פרטי חיפוש panel — the contact's search profile as ✓-chips with a
// completeness bar, inside an OverviewPanel (a card >= sm, a flat
// divider-separated section below it, per the page-wide convention).
//
// ONE treatment at every breakpoint, on purpose. A chrome-less definition-list
// variant for the desktop reference column was tried and reverted — the chip
// grid and the completeness bar ARE the value of this panel, and dropping them
// on desktop while the phone kept them was the wrong way round.
//
// TWO DISPLAY MODES, toggled for the WHOLE panel at once (product feedback
// 2026-08 — an anchored popover per chip was tried and rejected in review).
//
// ⚠️ THAT REJECTION WAS ABOUT DISPLAY, NOT EDITING, and the distinction is the whole
// reason this panel now has both. What was rejected was a popover for READING a chip's
// truncated value list — one that made you click to see what you already half-saw, per
// chip, with no way to see them all. The panel-wide expand replaced it and stays.
// Clicking a chip now opens the CRM's own single-FIELD editor instead, which is a
// different thing entirely and was asked for in those words: "I want us to be able to
// click on '+איזורים', '+הוסף אימייל' and all others, and actually see a small popup to
// write the value — just like we have in the table". Reading is the unfold toggle;
// writing is the chip. Do not re-merge them.
//
//   - Compact (default): the chip grid. A chip whose value list is truncated
//     (areas / property types / amenities beyond the inline slots) shows a
//     "+X" pill — the chip itself never grows.
//   - Expanded: one block per FILLED field — label line + EVERY value as
//     wrapped mini-chips (areas fully qualified, "פלורנטין, תל אביב") —
//     followed by the missing-field "+" chips. The unfold icon next to the
//     completeness caption toggles the mode; the "+X" pill is a COUNT and no
//     longer a control (clicking a chip edits it — see below).
// Clicking a chip/block opens that field's inline editor (edit/InlineFieldEdit —
// the SAME popup the CRM table uses, not a second implementation). The full
// EditContactDialog remains the fallback for a record the viewer does not own,
// and the only route to the fields no chip represents.
//
// AREAS COME FROM `interestedInLocations`, the source of truth the editor
// writes, NOT from the interestedIn*Distinct mirrors. Those mirrors are
// stamped asynchronously by the flattenInterestedInLocations Cloud Function,
// so reading them made the panel show STALE areas for seconds (or forever, if
// the CF lagged) after an edit — the reported "the search details don't
// refresh" bug. The distinct arrays remain only as a legacy fallback for
// contacts that carry no locations array. Reading locations directly also
// keeps city-only entries visible next to neighborhood entries (the mirrors
// hid every city the moment one neighborhood existed).
//
// Rent-vs-sale follows the app-wide convention (שוכר without קונה → rent),
// same inference ListingsMatchContext uses for prefill.
//
// DIRECTION: chip LABELS are Hebrew and stay RTL; chip VALUES pick their own
// direction via `valueDir` — "3+" / "עד 3" have no strong character issue, but
// a RANGE must read min-first under RTL: the min belongs on the RIGHT. Range
// values are therefore composed with RLM-wrapped dashes (see rtlRange) and
// rendered dir="rtl", the same technique as formatBudgetRangeShortRtl.
import { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography
} from "@material-ui/core";
import { UnfoldLess, UnfoldMore } from "@material-ui/icons";
import { alpha, useTheme } from "@material-ui/core/styles";
import { usePropertyTypes } from "hooks/usePropertyTypes";
import { amenityLabel } from "utils/contacts/amenityLabels";
import useAuth from "hooks/useAuth";
import useContactBook from "hooks/contacts/useContactBook";
import { InlineFieldEdit } from "./edit/InlineFieldEdit";
import { buildContactEditSubject } from "./edit/contactEditSubject";
import { canInlineEditContact } from "./edit/contactEditAccess";
import { EditContactDialog } from "../../../modal/EditContactDialog";
import { OverviewPanel } from "./OverviewPanel";
import { valueDir } from "./valueDirection";
import { formatCompactBudget } from "./overviewData";
import { getMinMaxSquareMeterString } from "utils/Haim/utils";
import {
  getContactCustomerTypes,
  genericPropertyDetailsKeys
} from "../../../../../../@types/sharedSchema";
import type { CrmEditableDimension } from "components/_dashboard/crm/table/edit/crmCellEditModel";
import type { RealEstateContact } from "../../../../../../@types/sharedSchema";

type OverviewSearchProfilePanelProps = {
  contact: RealEstateContact;
};


// How many list values render inline before the compact chip folds the rest
// into a "+X" pill. The pill is informational — it says how many values the
// chip is not showing; seeing them is the header's unfold toggle.
const INLINE_AREAS = 2;
const INLINE_TYPES = 2;
const INLINE_AMENITIES = 3;

// Legacy / malformed contact docs can hold a non-array value in fields we
// expect to be arrays (interestedInLocations, interestedInPropertyTypes, the
// distinct mirrors). `?? []` only guards null/undefined — a stored string
// slips through and crashes the whole overview tab on `.filter`/`.map`.
// Same guard as EditContactDialog's asArray.
function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

type ProfileChip = {
  id: string;
  label: string;
  /**
   * Which editor this chip opens. Named ON THE CHIP rather than mapped from `id` in the
   * renderer, so the sale/rent decision lives in the one place that already makes it —
   * `buildProfileChips`, which picked the budget FIELDS from the same `isRent` flag. A
   * lookup table in the renderer could disagree with the value on screen, and the way it
   * would fail is a realtor typing a rent budget into `customerBudget`.
   */
  dimension: CrmEditableDimension;
  value?: string; // present == filled (✓ chip); absent == missing (+ chip)
  /** Full value list — rendered in the expanded view for list chips. */
  values?: string[];
  /** How many of `values` the compact summary hides; > 0 shows the "+X" pill. */
  hiddenCount?: number;
  /** Explicit direction override for the rendered value (RTL ranges). */
  valueDirection?: "rtl" | "ltr" | "auto";
};

// RIGHT-TO-LEFT MARK. A range like "₪1.5M–₪2.5M" is digits/Latin only, so its
// two runs and the dash between them collapse into ONE left-to-right bidi run
// and render min-on-the-LEFT even inside the RTL page. Wrapping the dash in
// RLMs makes it inherit the RTL context, which places the first (min) run
// RIGHTMOST — the RTL reading order of a range. The composed string must be
// rendered with dir="rtl" (dir="auto" resolves LTR from the first Latin/digit).
const RLM = "‏";
const rtlRange = (min: string, max: string): string => `${min}${RLM}–${RLM}${max}`;

/** The contact's true boolean amenity keys, as Hebrew labels. */
const amenityLabelsOf = (contact: RealEstateContact): string[] =>
  genericPropertyDetailsKeys
    .filter((key) => contact[key] === true)
    .map((key) => amenityLabel(key));

type AreaEntry = {
  /** Compact name for the inline chip summary (neighborhood / street / city). */
  short: string;
  /** Fully-qualified label for the expanded view ("פלורנטין, תל אביב"). */
  full: string;
};

/**
 * Every interested-location, from the SOURCE array the editor writes (live —
 * refreshes the instant a save lands). The CF-derived distinct mirrors are a
 * fallback for legacy contacts only.
 */
const areaEntriesOf = (contact: RealEstateContact): AreaEntry[] => {
  const fromLocations = asArray<NonNullable<RealEstateContact["interestedInLocations"]>[number]>(
    contact.interestedInLocations
  )
    .map((location) => {
      const area = location.neighborhood || location.street || "";
      const short = area || location.city || "";
      if (!short) return null;
      const full = area && location.city ? `${area}, ${location.city}` : short;
      return { short, full };
    })
    .filter((entry): entry is AreaEntry => entry !== null);
  if (fromLocations.length) {
    const seen = new Set<string>();
    return fromLocations.filter((entry) =>
      seen.has(entry.full) ? false : (seen.add(entry.full), true)
    );
  }
  const neighborhoods = asArray<string>(contact.interestedInNeighborhoodsDistinct).filter(Boolean);
  const cities = asArray<string>(contact.interestedInCitiesDistinct).filter(Boolean);
  const legacy = neighborhoods.length ? neighborhoods : cities;
  return Array.from(new Set(legacy)).map((name) => ({ short: name, full: name }));
};

/** The profile as chips — 6 scalars plus one folded "מאפיינים" summary. */
function buildProfileChips(contact: RealEstateContact, isRent: boolean): ProfileChip[] {
  const chips: ProfileChip[] = [];

  // 1 — budget (rent: monthly ₪/ח׳; sale: compact millions). A range reads
  // min-first from the RIGHT under RTL (customer feedback 2026-08).
  const budgetMax = isRent ? contact.customerBudgetRent : contact.customerBudget;
  /**
   * ⚠️ A FLOOR OF ZERO IS A FLOOR, so the low bound is tested with `!= null` while the
   * max stays truthy-tested. The asymmetry is deliberate and matches what the two
   * numbers mean: nobody carries a 0 maximum (a 0 budget is no budget — the measured
   * platform rule behind `isSetNumber`), whereas "0 – 1.5M" is a hard ceiling with no
   * floor, and is exactly the range the contact dialog's slider produces at its left
   * end. Read as truthy, such a contact showed a bare "₪1.5M" here — the same lie the
   * dialog used to tell by reopening it in 'exact' mode.
   */
  const rawBudgetMin = isRent ? contact.customerBudgetRentMin : contact.customerBudgetMin;
  const budgetMin = typeof rawBudgetMin === "number" && isFinite(rawBudgetMin) ? rawBudgetMin : null;
  const hasBudgetMin = budgetMin !== null;
  const budgetSuffix = isRent ? "/ח׳" : "";
  chips.push({
    id: "budget",
    label: "תקציב",
    // The RANGE dimensions, never the table's scalar `budget` — that one reads the
    // mirror's COALESCE column and would show a range the contact never entered. And the
    // rent/sale choice is made HERE, from the same `isRent` that chose the fields above.
    dimension: isRent ? "budgetRentRange" : "budgetRange",
    /**
     * ⚠️ EITHER BOUND MAKES THIS FIELD FILLED, not the maximum alone.
     *
     * Keying "filled" on `budgetMax` made the chip disagree with the editor it opens: a
     * contact carrying only a FLOOR — which the dialog's range pair produces the moment
     * someone sets "מ־" and leaves "עד" blank — rendered the dashed `+ תקציב`, counted
     * itself missing in "N מתוך 6" and in the completeness bar, and then opened a popup
     * already holding 1.5M. A panel that says a value does not exist and then shows it is
     * worse than either answer on its own.
     *
     * A floor-only budget renders like the rooms chip's floor-only case ("3+"), which is
     * the same fact in the same shape two chips apart.
     */
    ...(budgetMax
      ? hasBudgetMin
        ? {
            value: `${rtlRange(
              formatCompactBudget(budgetMin as number),
              formatCompactBudget(budgetMax)
            )}${budgetSuffix}`,
            valueDirection: "rtl" as const
          }
        : { value: `${formatCompactBudget(budgetMax)}${budgetSuffix}` }
      : // A floor of 0 with no ceiling says nothing at all — that is the empty profile,
        // not a "₪0+" budget — so the floor-only chip keeps its positive test.
      budgetMin
      ? { value: `${formatCompactBudget(budgetMin)}${budgetSuffix}+` }
      : {})
  });

  // 2 — rooms.
  const { roomsMin, roomsMax } = contact;
  chips.push({
    id: "rooms",
    label: "חדרים",
    dimension: "rooms",
    ...(roomsMin && roomsMax
      ? roomsMin === roomsMax
        ? { value: `${roomsMin}` }
        : { value: rtlRange(`${roomsMin}`, `${roomsMax}`), valueDirection: "rtl" as const }
      : roomsMin
      ? { value: `${roomsMin}+` }
      : roomsMax
      ? { value: `עד ${roomsMax}` }
      : {})
  });

  // 3 — built area (שטח בנוי). Its own chip, beside rooms, because listing matching acts on it:
  // a listing well under the stated minimum is not offered to this contact at all. Until
  // 2026-10-11 the field lived only behind "פרטים נוספים" in the full dialog and nothing read it
  // (1,163 of 5.2M contacts had one) — a dashed "+ שטח" on every buyer's card is how a realtor
  // finds out the system now cares.
  const sizeValue = getMinMaxSquareMeterString(contact.squareMeterMin, contact.squareMeterMax);
  chips.push({
    id: "size",
    label: "שטח",
    dimension: "size",
    // The value is Hebrew ("מ־100 מ״ר") and its range form carries its own direction marks.
    ...(sizeValue ? { value: sizeValue, valueDirection: "rtl" as const } : {})
  });

  // 4 — areas: EVERY interested location (not just the first) — inline up to
  // INLINE_AREAS short names, the full qualified list in the expanded view.
  const areaEntries = areaEntriesOf(contact);
  chips.push({
    id: "areas",
    label: "אזורים",
    // `cities` and `neighborhoods` are two table COLUMNS over one document field; from
    // here there is one chip, so either name opens the same picker and writes the same
    // whole array.
    dimension: "cities",
    ...(areaEntries.length
      ? {
          value: areaEntries
            .slice(0, INLINE_AREAS)
            .map((entry) => entry.short)
            .join(", "),
          values: areaEntries.map((entry) => entry.full),
          hiddenCount: Math.max(0, areaEntries.length - INLINE_AREAS)
        }
      : {})
  });

  // 5 — property types.
  const types = asArray<string>(contact.interestedInPropertyTypes).filter(Boolean);
  chips.push({
    id: "propertyTypes",
    label: "סוג נכס",
    dimension: "propertyTypes",
    ...(types.length
      ? {
          value: types.slice(0, INLINE_TYPES).join(", "),
          values: types,
          hiddenCount: Math.max(0, types.length - INLINE_TYPES)
        }
      : {})
  });

  // 6 — floors.
  const { floorsMin, floorsMax } = contact;
  const hasFloors = floorsMin !== null && floorsMin !== undefined;
  const hasFloorsMax = floorsMax !== null && floorsMax !== undefined;
  chips.push({
    id: "floors",
    label: "קומה",
    dimension: "floors",
    ...(hasFloors && hasFloorsMax
      ? floorsMin === floorsMax
        ? { value: `${floorsMin}` }
        : { value: rtlRange(`${floorsMin}`, `${floorsMax}`), valueDirection: "rtl" as const }
      : hasFloors
      ? { value: `${floorsMin}+` }
      : hasFloorsMax
      ? { value: `עד ${floorsMax}` }
      : {})
  });

  // 7 — amenities (true boolean property keys).
  const amenities = amenityLabelsOf(contact);
  chips.push({
    id: "amenities",
    label: "מאפיינים",
    dimension: "amenities",
    ...(amenities.length
      ? {
          value: amenities.slice(0, INLINE_AMENITIES).join(", "),
          values: amenities,
          hiddenCount: Math.max(0, amenities.length - INLINE_AMENITIES)
        }
      : {})
  });

  return chips;
}

export function OverviewSearchProfilePanel({
  contact
}: OverviewSearchProfilePanelProps): JSX.Element {
  const theme = useTheme();
  const { propertyTypes } = usePropertyTypes();
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const openEdit = () => setEditOpen(true);

  /**
   * INLINE EDITING — every chip opens the CRM's own field popup rather than the whole
   * dialog (design feedback: "I want to be able to click on '+איזורים', '+הוסף אימייל'
   * and all others, and actually see a small popup to write the value — just like we have
   * in the table"). See `edit/InlineFieldEdit.tsx`.
   *
   * Gated on OWNERSHIP, not on permission: a colleague's record in the CRM side panel is
   * read once with no live listener, so an edit there would save and leave the chip
   * showing the old value. Those fall back to the dialog, which refetches for itself.
   */
  // The viewer's BOOK as well as their email — a pooled office record is "mine" through
  // its holder list (see `canInlineEditContact`).
  const { ownBookKey } = useContactBook();
  const canEditInline = canInlineEditContact(contact, user?.email as string | undefined, ownBookKey);
  const subject = useMemo(() => buildContactEditSubject(contact), [contact]);

  // Panel-wide display mode — expanding is ALL-fields-at-once, from the header unfold
  // toggle. Session-transient on purpose.
  const [expandedAll, setExpandedAll] = useState(false);

  const customerTypes = getContactCustomerTypes(contact);
  const isRent = customerTypes.includes("שוכר") && !customerTypes.includes("קונה");

  const chips = useMemo(() => buildProfileChips(contact, isRent), [contact, isRent]);
  const filledChips = chips.filter((chip) => chip.value !== undefined);
  const missingChips = chips.filter((chip) => chip.value === undefined);
  const completeness = Math.round((filledChips.length / chips.length) * 100);

  const title = isRent ? "✦ פרטי חיפוש — שכירות" : "✦ פרטי חיפוש";
  const headerEnd = (
    <Stack direction="row" alignItems="center" sx={{ gap: 0.25, flexShrink: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {filledChips.length} מתוך {chips.length}
      </Typography>
      {filledChips.length > 0 && (
        <Tooltip title={expandedAll ? "תצוגה מקוצרת" : "הצגת כל הפרטים"}>
          <IconButton
            size="small"
            onClick={() => setExpandedAll((prev) => !prev)}
            aria-label={expandedAll ? "תצוגה מקוצרת" : "הצגת כל פרטי החיפוש"}
          >
            {expandedAll ? (
              <UnfoldLess sx={{ fontSize: 18 }} />
            ) : (
              <UnfoldMore sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  // The mini value chip shared by the expanded blocks (one per value).
  const valueChipSx = {
    px: 1,
    py: 0.375,
    borderRadius: 4,
    typography: "body2",
    fontWeight: 600,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`
  } as const;

  // The dashed "+ field" chip for a missing field — shared by both modes.
  const renderMissingChip = (chip: ProfileChip): JSX.Element => (
    <InlineFieldEdit
      key={chip.id}
      subject={subject}
      dimension={chip.dimension}
      label={chip.label}
      disabled={!canEditInline}
      render={(openInline, isOpen) => (
        <ButtonBase
          // `openInline` is a no-op when inline editing is off, so the dialog is the
          // fallback rather than a second, competing route.
          onClick={canEditInline ? openInline : openEdit}
          aria-label={`הוספת ${chip.label}`}
          sx={{
            px: 1.25,
            py: 0.5,
            gap: 0.5,
            borderRadius: 4,
            typography: "body2",
            maxWidth: "100%",
            border: `1px dashed ${alpha(theme.palette.grey[500], 0.48)}`,
            color: "text.secondary",
            // The open chip stays visibly the thing the popup belongs to — without it a
            // surface anchored under a dashed outline reads as floating free.
            ...(isOpen ? { bgcolor: alpha(theme.palette.primary.main, 0.16) } : null),
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16) }
          }}
        >
          <span>+ {chip.label}</span>
        </ButtonBase>
      )}
    />
  );

  const editDialog = (
    <EditContactDialog
      open={editOpen}
      contact={contact}
      propertyTypes={propertyTypes}
      onClose={() => setEditOpen(false)}
    />
  );

  return (
    <>
      <OverviewPanel title={title} headerEnd={headerEnd}>
        <LinearProgress
          variant="determinate"
          value={completeness}
          sx={{ mb: 1.5, height: 6, borderRadius: 3 }}
        />

        {expandedAll ? (
          // EXPANDED: one block per filled field — label line + every value as
          // wrapped mini-chips (nothing truncated); missing fields keep their
          // dashed "+" chips in a trailing row. Each block opens the editor.
          <Stack sx={{ gap: 0.75 }}>
            {filledChips.map((chip) => {
              const rowValues = chip.values ?? (chip.value ? [chip.value] : []);
              return (
                <InlineFieldEdit
                  key={chip.id}
                  subject={subject}
                  dimension={chip.dimension}
                  label={chip.label}
                  disabled={!canEditInline}
                  // The expanded rows are full-width blocks, not chips — see the prop.
                  display="block"
                  render={(openInline, isOpen) => (
                <ButtonBase
                  onClick={canEditInline ? openInline : openEdit}
                  aria-label={`עריכת ${chip.label}`}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    alignItems: "flex-start",
                    alignSelf: "stretch",
                    textAlign: "start",
                    gap: 0.5,
                    px: 1,
                    py: 0.75,
                    mx: -1,
                    borderRadius: 1.5,
                    ...(isOpen ? { bgcolor: alpha(theme.palette.primary.main, 0.06) } : null),
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) }
                  }}
                >
                  <Stack direction="row" alignItems="center" sx={{ gap: 0.5 }}>
                    <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                      ✓
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {chip.label}
                    </Typography>
                    {rowValues.length > 1 && (
                      <Typography variant="caption" color="text.secondary">
                        · {rowValues.length}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap" }}>
                    {rowValues.map((value) => (
                      <Box
                        key={value}
                        component="span"
                        // A list value picks its own direction; a lone scalar
                        // keeps the chip's explicit override (RTL ranges).
                        dir={
                          chip.values ? valueDir(value) : chip.valueDirection ?? valueDir(value)
                        }
                        sx={valueChipSx}
                      >
                        {value}
                      </Box>
                    ))}
                  </Stack>
                </ButtonBase>
                  )}
                />
              );
            })}

            {missingChips.length > 0 && (
              <Stack direction="row" sx={{ gap: 0.75, flexWrap: "wrap", mt: 0.5 }}>
                {missingChips.map(renderMissingChip)}
              </Stack>
            )}
          </Stack>
        ) : (
          // COMPACT: the chip grid. A truncated list chip still carries its "+X"
          // pill as a COUNT — but the click edits, like every other chip.
          //
          // It used to expand the whole panel instead, which meant three of the six chips
          // (אזורים / סוג נכס / מאפיינים — the ones with lists) swallowed the first click
          // and did something different from their neighbours. Now every chip in the grid
          // means the same thing, and seeing the full list is the header's unfold toggle,
          // which is where a display control belongs.
          <Stack direction="row" sx={{ gap: 0.75, flexWrap: "wrap" }}>
            {chips.map((chip) => {
              const isFilled = chip.value !== undefined;
              if (!isFilled) return renderMissingChip(chip);
              const isExpandable = (chip.hiddenCount ?? 0) > 0;
              return (
                <InlineFieldEdit
                  key={chip.id}
                  subject={subject}
                  dimension={chip.dimension}
                  label={chip.label}
                  disabled={!canEditInline}
                  render={(openInline, isOpen) => (
                <ButtonBase
                  onClick={canEditInline ? openInline : openEdit}
                  aria-label={`עריכת ${chip.label}`}
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    gap: 0.5,
                    borderRadius: 4,
                    typography: "body2",
                    // A chip with a long value (areas/types lists) must wrap
                    // inside itself instead of stretching the row past narrow
                    // mobile viewports.
                    maxWidth: "100%",
                    flexWrap: "wrap",
                    overflowWrap: "anywhere",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.32)}`,
                    bgcolor: alpha(theme.palette.primary.main, isOpen ? 0.16 : 0.08),
                    color: "text.primary",
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                  }}
                >
                  <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                    ✓
                  </Box>
                  <span>{chip.label}</span>
                  {/* The VALUE carries its own direction: a bare "3+" holds no
                      strong character (explicit ltr via valueDir), and a RANGE
                      is composed RLM-dashed + dir="rtl" so the MIN lands on
                      the RIGHT — the RTL reading order. */}
                  <Box
                    component="span"
                    dir={chip.valueDirection ?? valueDir(chip.value)}
                    sx={{ fontWeight: 600 }}
                  >
                    {chip.value}
                  </Box>
                  {isExpandable && (
                    <Box
                      component="span"
                      dir="ltr"
                      sx={{
                        px: 0.75,
                        borderRadius: 3,
                        fontSize: 12,
                        lineHeight: "18px",
                        fontWeight: 700,
                        bgcolor: alpha(theme.palette.primary.main, 0.16),
                        color: "primary.dark"
                      }}
                    >
                      +{chip.hiddenCount}
                    </Box>
                  )}
                </ButtonBase>
                  )}
                />
              );
            })}
          </Stack>
        )}
      </OverviewPanel>

      {/* Mounted ON DEMAND — it used to render with `open={false}` on every contact open,
          so its whole subtree (a 2,000-line form, its listing pickers and their state) was
          constructed for every contact anybody looked at. It remains the route to every
          field no chip here represents, and the fallback for a record the viewer does not
          own.

          ⚠️ This does NOT save the property-type facet request. `usePropertyTypes()` is a
          hook and runs unconditionally at the top of this component whatever the dialog
          does; moving that cost would mean lifting the hook to the tab, which is a
          separate change. Claiming the saving here would be the comment lying about the
          line under it. */}
      {editOpen && editDialog}
    </>
  );
}
